<template>
  <div class="tl">
    <Breadcrumbs main="Рейтинг" title="Рейтинг игроков"/>

    <div class="container-fluid tl-container">

      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingRatingForUsers }">
            <i class="fa fa-search tl-search__icon"></i>
            <input ref="search"
                   type="search"
                   class="tl-search__input tl-search__input--wide"
                   v-model="searchModel"
                   placeholder="ID, username, почта, команда, PUBG ID или ник…"
                   @keydown.esc.prevent="clearSearch"/>
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <div class="tl-spacer"></div>

          <button type="button" class="tl-btn-ghost"
                  :disabled="isLoadingDownloadTopPlayers"
                  @click="downloadTopPlayers">
            <i class="fa" :class="isLoadingDownloadTopPlayers ? 'fa-spinner rt-spin' : 'fa-download'"></i>
            {{ isLoadingDownloadTopPlayers ? 'Готовим файл…' : 'Топ 20' }}
          </button>

          <button type="button" class="tl-btn-primary"
                  :disabled="isLoadingActualization"
                  @click="submitForm">
            <i class="fa" :class="isLoadingActualization ? 'fa-spinner rt-spin' : 'fa-refresh'"></i>
            {{ isLoadingActualization ? 'Идёт актуализация…' : 'Актуализировать' }}
          </button>
        </div>

        <div class="rt-meta">
          <span v-if="actualizedRatingTime">
            Рейтинг актуализирован: <b>{{ formatDateTime(actualizedRatingTime) }}</b>
          </span>
          <span v-if="searchApplied" class="rt-meta__chip">
            Поиск: «{{ searchApplied }}»
            <button type="button" @click="clearSearch"><i class="fa fa-times"></i></button>
          </span>
        </div>
      </div>

      <div class="tl-panel">
        <div v-if="isLoadingRatingForUsers" class="tl-skeleton">
          <div v-for="n in 8" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--sm"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-user-o"></i></div>
          <div class="tl-empty__title">{{ searchApplied ? 'Ничего не нашлось' : 'Рейтинга пока нет' }}</div>
          <div class="tl-empty__text">
            {{ searchApplied
              ? 'Поиск работает по ID, username, почте, названию команды и игровым данным.'
              : 'Игроки появятся здесь после актуализации рейтинга.' }}
          </div>
          <div v-if="searchApplied" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost" @click="clearSearch">Сбросить поиск</button>
          </div>
        </div>

        <div v-else class="tl-table-wrap">
          <b-table class="tl-table rt-table"
                   responsive
                   hover
                   borderless
                   show-empty
                   :items="rows"
                   :fields="fields"
                   @row-clicked="openHistory">
            <template #cell(place)="{ index }">
              <span class="rt-place" :class="placeClass(index)">{{ placeNumber(index) }}</span>
            </template>

            <template #cell(username)="{ item }">
              <div class="rt-team">
                <div>
                  <router-link class="rt-team__name"
                               :to="{ name: 'user_ratings_info', params: { id: item.id } }"
                               @click.native.stop>{{ item.username || 'без ника' }}</router-link>
                  <button type="button" class="tl-id rt-user__id" @click.stop="copyId(item.id)"
                          v-b-tooltip.hover.right="'Скопировать ID игрока'">
                    <span>#{{ item.id }}</span>
                    <i class="fa fa-copy"></i>
                  </button>
                </div>
              </div>
            </template>

            <template #cell(pubgNick)="{ item }">
              <span v-if="pubgNick(item)">{{ pubgNick(item) }}</span>
              <span v-else class="rt-dim">нет игрового профиля</span>
            </template>

            <!-- было: кнопка-раскрывашка с иконкой, команды прятались за кликом -->
            <template #cell(teams)="{ item }">
              <div v-if="(item.teams || []).length" class="rt-chips">
                <span v-for="team in visibleTeams(item)" :key="'team' + team.id" class="rt-chip">
                  {{ team.name }}
                </span>
                <span v-if="hiddenTeamsCount(item)" class="rt-chip rt-chip--primary"
                      v-b-tooltip.hover.top="hiddenTeamNames(item)">
                  +{{ hiddenTeamsCount(item) }}
                </span>
              </div>
              <span v-else class="rt-dim">—</span>
            </template>

            <template #cell(rating)="{ item }">
              <span class="rt-rating">{{ formatRating(item) }}</span>
            </template>

            <template #cell(action)="{ item }">
              <router-link class="tl-icon-btn rt-open"
                           :to="{ name: 'user_ratings_info', params: { id: item.id } }"
                           @click.native.stop
                           v-b-tooltip.hover.left="'История начислений'">
                <i class="fa fa-angle-right"></i>
              </router-link>
            </template>
          </b-table>
        </div>

        <div v-if="!isLoadingRatingForUsers && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ total || 0 }}</b>
          </div>
          <b-pagination class="tl-pagination my-0"
                        v-model="currentPageModel"
                        :total-rows="total"
                        :per-page="perPage"
                        :limit="7"
                        @change="handlePageChange"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import {UsersRatingAdapterMixin} from "@/mixins/Rating/UserRatingAdapterMixin";
import Vue from "vue";
import api from "@/api/api";
import moment from 'moment'
import lodash from 'lodash'

export default {
  name: "ListUsersRating",
  mixins: [PaginationHelperMixin, UsersRatingAdapterMixin],
  components: {},
  created() {
    try {
      this.isLoadingRatingForUsers = true,
      (async () => {
        this.recordPageToStorage(this.currentPage, this.pageKey)
        this.grabUsersRating({
          pageNumber: this.getCurrentPageNumber(this.pageKey),
        })
        const actualizedUserRatingTime = api.usersRating.actualizedTime().then((response) => {
          this.actualizedRatingTime = response.data.data.lastActualizeDatetime || '';
        });

        const gameList = api.games.index().then((gameList) => {
          this.gameId = gameList.data.games.find((game) => game.slug === 'pubg-mobile').id;
        });

        await Promise.all([actualizedUserRatingTime, gameList])
      })()
    } finally {
      this.isLoadingRatingForUsers = false
    }
  },
  data() {
    return {
      pageKey: 'usersRatingPageNumber',
      perPage: 10,
      debouncedSearch: lodash.debounce(function () {
        this.getFilteredUsersRating();
      }, 600),
      fields: [
        {key: 'place', label: '#', sortable: false, thClass: 'rt-th--place'},
        {key: 'username', label: 'Игрок', sortable: true},
        {key: 'pubgNick', label: 'PUBG ник', sortable: true},
        {key: 'teams', label: 'Команды', sortable: false},
        {key: 'rating', label: 'Рейтинг', sortable: true, thClass: 'rt-th--num'},
        {key: 'action', label: '', sortable: false, thClass: 'tl-th--act', tdClass: 'tl-td--act'},
      ],
      isLoadingActualization: false,
      actualizedRatingTime: '',
      gameId: null,
      isLoadingRatingForUsers: false,
      searchForUsersRatingModel: '',
      isLoadingDownloadTopPlayers: false,
      excelFile: null
    };
  },
  computed: {
    rows() {
      return this.usersRating || [];
    },

    searchApplied() {
      return (this.searchForUsersRatingModel || '').trim();
    },

    searchModel: {
      get() { return this.searchForUsersRatingModel },
      set(value) {
        this.searchForUsersRatingModel = value;
        this.debouncedSearch();
      }
    },

    currentPageModel: {
      get() { return Number(this.currentPage) || 1 },
      set(value) { this.currentPage = value }
    },

    rangeFrom() {
      if (!this.rows.length) return 0;
      return (this.currentPageModel - 1) * this.perPage + 1;
    },

    rangeTo() {
      return (this.currentPageModel - 1) * this.perPage + this.rows.length;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.focusSearch);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.focusSearch);
  },
  methods: {
    focusSearch(event) {
      if (event.key !== '/' || ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return;
      event.preventDefault();
      if (this.$refs.search) this.$refs.search.focus();
    },

    clearSearch() {
      this.searchForUsersRatingModel = '';
      this.getFilteredUsersRating();
    },

    placeNumber(index) {
      return (this.currentPageModel - 1) * this.perPage + index + 1;
    },

    placeClass(index) {
      const place = this.placeNumber(index);
      if (place === 1) return 'rt-place--gold';
      if (place === 2) return 'rt-place--silver';
      if (place === 3) return 'rt-place--bronze';
      return '';
    },

    pubgNick(item) {
      const profiles = item.gameProfiles || [];
      const profile = profiles.find((gameProfile) => gameProfile.game && gameProfile.game.slug === 'pubg-mobile');
      return (profile && profile.fields && profile.fields.nick) || '';
    },

    formatRating(item) {
      const rating = item.ratingPubgMobile && item.ratingPubgMobile.rating;
      return Number(rating || 0).toFixed(2);
    },

    visibleTeams(item) {
      return (item.teams || []).slice(0, 2);
    },

    hiddenTeamsCount(item) {
      return Math.max((item.teams || []).length - 2, 0);
    },

    hiddenTeamNames(item) {
      return (item.teams || []).slice(2).map((team) => team.name).join(', ');
    },

    openHistory(item) {
      this.$router.push({name: 'user_ratings_info', params: {id: item.id}});
    },

    copyId(id) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(() => {
          this.$toasted.success('ID игрока скопирован', {position: 'top-right', duration: 1500});
        }).catch(() => {
        });
      }
    },

    formatDateTime(value) {
      if (!value) return '—';
      const date = new Date(String(value).replace(' ', 'T'));
      if (isNaN(date.getTime())) return String(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    async submitForm() {
      try {
        this.isLoadingActualization = true
        await api.usersRating.actualize()
        Vue.toasted.success("Успешно актуализирован", {
          position: 'top-right',
          duration: 3000
        })
      } catch (error) {
          Vue.toasted.error(error.response.data.message || "Не удалось актуализировать", {
            position: 'top-right',
            duration: 3000
          });
      } finally {
        this.isLoadingActualization = false
      }
    },
    async downloadTopPlayers() {
      try {
        this.isLoadingDownloadTopPlayers = true
        const createResponse = await api.excel.create();
        const fileId = createResponse.data.export.id;

        const downloadResponse = await api.excel.index(fileId, {
          method: 'GET',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          responseType: 'blob'
        });
        // get current date
        let currentDate = new Date();
        let formattedDate = moment(currentDate).format('DD.MM.YYYY');

        const link = document.createElement('a');
        link.href = URL.createObjectURL(downloadResponse.data);
        link.setAttribute('download', `${formattedDate }_top20_players.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        Vue.toasted.success("Успешно скачано", {
          position: 'top-right',
          duration: 3000
        })
      } catch (error) {
          Vue.toasted.error(error.response.data.message || "Не удалось скачать", {
            position: 'top-right',
            duration: 3000
          });
      } finally {
          this.isLoadingDownloadTopPlayers = false
      }
    },
    handlePageChange(pageNumber) {
      this.recordPageToStorage(pageNumber, this.pageKey)
      this.grabUsersRating({
        pageNumber: pageNumber,
        ...(this.searchForUsersRatingModel && {search: this.searchForUsersRatingModel}),
      })
    },
    getFilteredUsersRating() {
      this.isLoadingRatingForUsers = true
      this.grabUsersRating({search: this.searchForUsersRatingModel})
          .then(() => {
            this.isLoadingRatingForUsers = false
          })
    },
  },

};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
@import "~@/assets/scss/rating-list.scss";

.tl {
  @include rating-list-parts;

  /* поиск по нескольким полям — строке нужно больше места */
  .tl-search__input--wide { min-width: 320px; }

  .rt-user__id { margin-top: 3px; }
}
</style>
